import { createORPCClient, ORPCError } from "@orpc/client";
import type { RouterContractClient } from "@orpc/contract";
import { OpenAPILink } from "@orpc/openapi/fetch";
import {
  type apiContract,
  assistedNoteConflictSchema,
  transcriptConflictSchema,
} from "@rosc/contracts";
import router from "@rosc/contracts/contract.json";
import { t } from "i18next";
import { z } from "zod";
import { env } from "@/env";
import { requestAdminStepUp } from "@/lib/admin-access";
import {
  displayLanguageHeader,
  getNativeIdentity,
  impersonationReadOnlyMessage,
} from "@/lib/native-auth";

const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    data: z.unknown().optional(),
  }),
});
type ReferenceClient = RouterContractClient<
  Pick<
    typeof apiContract,
    | "registerAccount"
    | "provisionMyAccount"
    | "occupations"
    | "settingsOptions"
    | "me"
    | "adminOverview"
    | "adminUsers"
    | "adminUser"
    | "adminAudit"
    | "adminGrantRole"
    | "adminRevokeRole"
    | "adminBan"
    | "adminUnban"
    | "adminRemove"
    | "adminImpersonate"
    | "adminSetPassword"
    | "ownUsage"
    | "ownActivity"
    | "ownDevices"
    | "updateMe"
    | "updateMyPreferences"
    | "myDefaultTemplate"
    | "setMyDefaultTemplate"
    | "replaceMyEmail"
    | "macros"
    | "createMacro"
    | "updateMacro"
    | "deleteMacro"
    | "createVisit"
    | "createPreChart"
    | "changePreChartTemplate"
    | "getVisitAudio"
    | "replaceVisitAudio"
    | "createAudioUpload"
    | "completeAudioUpload"
    | "createRecording"
    | "createVisitNote"
    | "createTranscription"
    | "createBillingSession"
    | "createRecordingSession"
    | "createTemplateProposal"
    | "createDictationReport"
    | "createDictatedReport"
    | "updateDictatedReport"
    | "createNoteEditProposal"
    | "listVisitSources"
    | "createVisitSource"
    | "deleteVisitSource"
    | "readVisitSource"
    | "identifyVisitSpeakers"
    | "updateVisitSpeaker"
    | "updateVisitTranscript"
    | "submitFeedbackReport"
    | "libraryCategories"
    | "libraryTemplates"
    | "listTemplates"
    | "getTemplate"
    | "createTemplate"
    | "updateTemplate"
    | "deleteTemplate"
    | "listVisits"
    | "getVisit"
    | "listVisitRecordings"
    | "listVisitNotes"
    | "getVisitVersion"
    | "updateVisit"
    | "deleteVisit"
    | "updateVisitNote"
    | "listVocabulary"
    | "addVocabulary"
    | "updateVocabularyTerm"
    | "deleteVocabularyTerm"
    | "getVisitInstructions"
    | "undoVisitInstructions"
    | "restoreVisitNote"
    | "getVisitNoteSources"
    | "getVisitTranscriptSegments"
    | "getVisitTranscriptFlags"
    | "listReferralTemplates"
    | "createReferralTemplate"
    | "updateReferralTemplate"
    | "deleteReferralTemplate"
    | "createReferralTemplateExtraction"
    | "createTemplateExtraction"
    | "listFormTemplates"
    | "createFormTemplate"
    | "updateFormTemplate"
    | "deleteFormTemplate"
    | "listVisitForms"
    | "createVisitForm"
    | "getVisitForm"
    | "updateVisitForm"
    | "getVisitFormPdf"
    | "deleteVisitForm"
    | "createGapTranscription"
  >,
  { expectedUserUid?: string }
>;

export const referenceClient = createORPCClient<ReferenceClient>(
  new OpenAPILink(router, {
    origin: () => new URL(env.VITE_API_V2_BASE_URL).origin,
    headers: async (options, path): Promise<Record<string, string>> => {
      options.signal?.throwIfAborted();
      if (path[0] === "occupations") return {};
      // Registration sends the welcome email in the display language.
      if (path[0] === "registerAccount") return displayLanguageHeader();
      // Account setup runs before an app identity exists; the session cookie authenticates it.
      if (path[0] === "provisionMyAccount") return displayLanguageHeader();
      const user = getNativeIdentity();
      if (
        !user ||
        (options.context.expectedUserUid &&
          user.uid !== options.context.expectedUserUid)
      )
        throw new ORPCError("UNAUTHORIZED", {
          message: t("auth.native.errors.accountChanged"),
        });
      return {};
    },
    fetch: (request, init) =>
      fetch(request, {
        ...init,
        credentials: "include",
      }),
    customErrorResponseBodyDecoder: (
      body,
      response,
    ): ORPCError<string, unknown> | undefined => {
      const parsed = apiErrorSchema.safeParse(body);
      if (!parsed.success) return undefined;
      if (parsed.data.error.code === "ADMIN_STEP_UP_REQUIRED")
        requestAdminStepUp();
      const review =
        response.status === 409 && parsed.data.error.code === "CONFLICT"
          ? assistedNoteConflictSchema
              .or(transcriptConflictSchema)
              .safeParse(parsed.data.error.data)
          : null;
      return Object.assign(
        new ORPCError(parsed.data.error.code, {
          message:
            parsed.data.error.code === "IMPERSONATION_READ_ONLY"
              ? impersonationReadOnlyMessage
              : parsed.data.error.message,
          ...(review?.success ? { data: review.data } : {}),
        }),
        { status: response.status },
      );
    },
  }),
);
